// 📸 스마트폰 썸네일 단일 리졸버 (SSOT: public/assets 실존 파일 매니페스트)
// [정책 — 2026-07-23 확정]
//  1) 모델 정규키 완전 일치 시 해당 이미지
//  2) 카탈로그 image 필드는 매니페스트에 실존할 때만 채택
//  3) 같은 세대 내 변형(variant) 완화 허용: "Z Flip3 5G"→ZFLIP3, "프로 맥스"→프로→기본
//  4) 그 외(세대 이미지 없음·A/J처럼 라인 이미지 전무)는 공용 기본 이미지
//     ⚠️ 인접 세대 이미지 대체 금지 (오해 방지 — 사용자 결정)
// 매니페스트 재생성: npm run gen:images
import { PHONE_IMAGE_ENTRIES, PhoneImageEntry } from '../constants/phoneImageManifest';

export interface PhoneResolveInput {
  brand?: string; // 'Apple' | 'Samsung' (생략 시 이름에서 추정)
  series?: string; // 'Galaxy S' | 'iPhone 15' 등
  modelEn?: string | null;
  modelKo?: string | null;
  catalogImage?: string | null;
}

// 공용 기본 이미지 (실존 파일). 교체 시 이 상수만 변경.
export const BRAND_DEFAULT_PHONE_IMAGE = '/assets/device_phones.png';
export const TABLET_DEFAULT_IMAGE_SAMSUNG = '/assets/device_tab.png';
export const TABLET_DEFAULT_IMAGE_APPLE = '/assets/ipad/iPadDefault.png';

const TABLET_LINES = new Set(['tabs', 'taba', 'tablegacy', 'pad', 'pro', 'air', 'mini']);

// ── 인덱스 구축 (모듈 로드 시 1회) ──────────────────────────
const byKey = new Map<string, PhoneImageEntry>();
const byLine = new Map<string, PhoneImageEntry[]>();
const byDecodedPath = new Map<string, PhoneImageEntry>();
const byNameKey = new Map<string, PhoneImageEntry>(); // 특수 모델 (버디·점프·와이드 등)
for (const e of PHONE_IMAGE_ENTRIES) {
  if (e.nameKey) {
    byNameKey.set(e.nameKey, e);
    // 'folder1' → '폴더'(1세대, 숫자 없는 모델명) 별칭
    if (/1$/.test(e.nameKey) && !byNameKey.has(e.nameKey.slice(0, -1))) {
      byNameKey.set(e.nameKey.slice(0, -1), e);
    }
  } else if (!e.pathOnly) {
    byKey.set(`${e.brand}|${e.line}|${e.gen}|${e.variant}|${e.size || ''}`, e);
    const lk = `${e.brand}|${e.line}`;
    if (!byLine.has(lk)) byLine.set(lk, []);
    byLine.get(lk)!.push(e);
  }
  byDecodedPath.set(decodeURIComponent(e.path), e);
  byDecodedPath.set(e.path, e);
}
for (const arr of byLine.values()) arr.sort((a, b) => a.genOrder - b.genOrder);

// ── 모델명 정규화 파이프라인 ────────────────────────────────
interface ParsedKey {
  brand: 'samsung' | 'apple';
  line: string; // s | flip | fold | note | a | j | m | iphone | tabs | taba | pad | pro | air | mini | '' ...
  gen: string;
  variants: string[]; // 등장 순서 유지
  size?: string; // 태블릿 화면 크기 ('10.5' → '105')
  nameKey: string; // 특수 모델 매칭용 (버디2 → 'buddy2')
  nameKeys?: string[]; // modelEn/modelKo 등 전체 후보의 nameKey 병합
}

const KO_TOKEN_MAP: Array<[RegExp, string]> = [
  [/프로\s*맥스/g, ' promax '],
  [/울트라/g, ' ultra '],
  [/플러스/g, ' plus '],
  [/프로/g, ' pro '],
  [/맥스/g, ' max '],
  [/미니/g, ' mini '],
  [/엣지/g, ' edge '],
  [/에어/g, ' air '],
  [/광대역/g, ' broadband '],
  [/갤럭시|갤러시/g, ' galaxy '], // '갤러시' 오타 데이터 포함
  [/아이폰/g, ' iphone '],
  [/아이패드/g, ' ipad '],
  [/인치/g, ' '],
  [/라이트/g, ' lite '],
  [/액티브/g, ' active '],
  [/s\s*펜|에스펜/g, ' spen '],
  [/노트/g, ' note '],
  [/플립/g, ' flip '],
  [/폴더/g, ' folder '], // ⚠️ '폴드'보다 먼저 치환해야 함
  [/폴드/g, ' fold '],
  [/탭/g, ' tab '],
  [/세대/g, ' '],
  // 특수 모델 (A/J 라인)
  [/버디/g, ' buddy '],
  [/와이드/g, ' wide '],
  [/점프/g, ' jump '],
  [/퀀텀/g, ' quantum '],
  [/골든/g, ' golden '],
  [/엑스\s*커버/g, ' xcover '],
  [/스타일/g, ' style '], // ⚠️ '스타'보다 먼저
  [/스타/g, ' star '],
  [/프라임/g, ' prime '],
  [/에이스/g, ' ace '],
  [/마이브/g, ' mive '],
  [/시나모롤/g, ' '],
  [/키즈폰/g, ' kidsphone '],
];

const VARIANT_TOKENS = new Set([
  'ultra', 'promax', 'pro', 'plus', 'plusplus', 'fe', 'e', '5g',
  'edge', 'mini', 'max', 'air', 'broadband', 'lite',
]);

// nameKey/variant 계산에서 제외할 토큰
const STOP_TOKENS = new Set(['galaxy', 'iphone', 'z', 'mive']);

const normalizeTokens = (raw: string): string[] => {
  let s = (raw || '').normalize('NFC').trim().toLowerCase();
  if (!s) return [];
  s = s.replace(/\+\+/g, ' plusplus ').replace(/\+/g, ' plus ');
  for (const [re, rep] of KO_TOKEN_MAP) s = s.replace(re, rep);
  s = s.replace(/[()[\],/]/g, ' '); // '.'은 유지 (태블릿 크기 10.5 등 소수 표기)
  s = s.replace(/\b5\s*g\b/g, ' __5g__ '); // '5g'가 숫자/문자 경계 분리로 쪼개지는 것 방지
  s = s.replace(/(\d)([a-z가-힣])/g, '$1 $2').replace(/([a-z가-힣])(\d)/g, '$1 $2');
  s = s.replace(/__\s*5\s*g\s*__|__5g__/g, ' 5g ');
  return s.split(/\s+/).map(t => t.replace(/^\.+|\.+$/g, '')).filter(Boolean);
};

export const parsePhoneModelKey = (input: PhoneResolveInput): ParsedKey | null => {
  const brandHint = (input.brand || '').toLowerCase();
  const candidates = [input.modelEn, input.modelKo, input.series].filter(Boolean) as string[];

  let best: ParsedKey | null = null;
  const nameKeys: string[] = [];
  for (const name of candidates) {
    const parsed = parseOneName(name, brandHint);
    if (!parsed) continue;
    if (parsed.nameKey && !nameKeys.includes(parsed.nameKey)) nameKeys.push(parsed.nameKey);
    if (!best || (parsed.gen && !best.gen)) best = parsed; // 세대까지 파싱된 결과 우선
  }
  if (!best) return null;
  // 모든 후보 이름의 nameKey를 병합 (modelEn/modelKo 표기가 다른 특수 모델 대응)
  return { ...best, nameKeys };
};

const TAB_VARIANT_TOKENS = new Set(['ultra', 'fe', 'plus', 'lite', '5g', 'spen']);
const IPAD_SIZE_INT = new Set(['11', '13']); // 프로/에어/미니에서 11·13은 세대가 아닌 인치

const parseOneName = (name: string, brandHint: string): ParsedKey | null => {
  const tokens = normalizeTokens(name);
  if (!tokens.length) return null;

  // ── 태블릿: 아이패드 ──
  if (tokens.includes('ipad')) {
    let line = 'pad';
    if (tokens.includes('pro')) line = 'pro';
    else if (tokens.includes('air')) line = 'air';
    else if (tokens.includes('mini')) line = 'mini';
    let gen = '';
    let size = '';
    for (const t of tokens) {
      if (/^\d{1,2}\.\d$/.test(t)) { if (!size) size = t.replace('.', ''); continue; }
      if (/^\d{1,2}$/.test(t)) {
        if (line !== 'pad' && IPAD_SIZE_INT.has(t)) { if (!size) size = t; }
        else if (!gen) gen = t;
      }
    }
    return { brand: 'apple', line, gen, variants: [], size, nameKey: '' };
  }

  // ── 태블릿: 갤럭시 탭 ──
  if (tokens.includes('tab')) {
    let line = '';
    let gen = '';
    let size = '';
    const variants: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      if (STOP_TOKENS.has(t) || t === 'tab') continue;
      if (/^\d{1,2}\.\d$/.test(t)) { if (!size) size = t.replace('.', ''); continue; }
      if (t === 's' && tokens[i + 1] === 'pen') { variants.push('spen'); i++; continue; } // 'S Pen' 분리 토큰
      if (!line && t === 's') {
        const next = tokens[i + 1];
        if (next && /^\d{1,2}$/.test(next)) { line = 'tabs'; gen = next; i++; } else line = 'tabs';
        continue;
      }
      if (!line && t === 'a') {
        line = 'taba';
        const next = tokens[i + 1];
        if (next && /^\d{1,2}$/.test(next)) { gen = next; i++; }
        continue;
      }
      if (TAB_VARIANT_TOKENS.has(t)) variants.push(t);
      else if (/^s\d{1,2}$/.test(t)) { line = 'tabs'; gen = t.slice(1); }
      else if (/^a\d{1,2}$/.test(t)) { line = 'taba'; gen = t.slice(1); }
      else if (line === 'tabs' && gen && t === 'e') variants.push('e'); // 탭 S5e
    }
    // 구형 탭(탭1~4)·액티브·프로·E 등 이미지 미보유 라인 → 기본 이미지 대상
    return { brand: 'samsung', line: line || 'tablegacy', gen, variants, size, nameKey: '' };
  }

  const isApple = brandHint.includes('apple') || tokens.includes('iphone');
  const isSamsung = !isApple && (brandHint.includes('samsung') || tokens.includes('galaxy')
    || tokens.includes('note') || tokens.includes('flip') || tokens.includes('fold'));

  if (isApple) {
    let gen = '';
    const variants: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      if (t === 'iphone') continue;
      if (!gen) {
        if (t === 'se') {
          const next = tokens[i + 1];
          if (next && /^[23](nd|rd|th)?$/.test(next)) { gen = 'se' + next[0]; i++; }
          else gen = 'se';
          continue;
        }
        if (t === 'xs' || t === 'xr' || t === 'x') { gen = t; continue; }
        if (/^\d{1,2}s$/.test(t)) { gen = t; continue; }
        if (/^\d{1,2}$/.test(t)) {
          // '아이폰 6 s' 처럼 분리된 s 결합
          if (tokens[i + 1] === 's') { gen = t + 's'; i++; } else gen = t;
          continue;
        }
      }
      if (VARIANT_TOKENS.has(t)) variants.push(t);
    }
    // pro + max 분리 토큰 → promax 결합
    const merged = mergeProMax(variants);
    return { brand: 'apple', line: 'iphone', gen, variants: merged, nameKey: '' };
  }

  if (isSamsung) {
    let line = '';
    let gen = '';
    const variants: string[] = [];
    const nameTokens: string[] = []; // 특수 모델 nameKey용
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      if (STOP_TOKENS.has(t)) continue;
      nameTokens.push(t);
      if (!line && (t === 'flip' || t === 'fold' || t === 'note')) {
        line = t;
        const next = tokens[i + 1];
        if (next && /^\d{1,2}$/.test(next)) { gen = next; nameTokens.push(next); i++; }
        else if (t !== 'note') gen = '1'; // 'Z 플립'/'폴드' 1세대(숫자 없음) → ZFLIP1/ZFOLD1 파일과 매칭
        continue;
      }
      if (!line && /^[sajm]$/.test(t)) {
        const next = tokens[i + 1];
        if (next && /^\d{1,3}$/.test(next)) { line = t; gen = next; nameTokens.push(next); i++; continue; }
        line = t; // 'Galaxy S'/'Galaxy A' 등 시리즈명 단독
        continue;
      }
      if (VARIANT_TOKENS.has(t)) variants.push(t);
      else if (/^[sajm]\d{1,3}$/.test(t)) { line = t[0]; gen = t.slice(1); } // 's24' 결합형
      else if (line && gen && /^[a-z]+$/.test(t)) variants.push(t); // 잔여 접미(A21s의 s, A8 STAR의 star 등)
    }
    const nameKey = nameTokens.join('');
    if (!line && !nameKey) return null;
    return { brand: 'samsung', line, gen, variants, nameKey };
  }

  return null;
};

const mergeProMax = (variants: string[]): string[] => {
  const out = [...variants];
  const pi = out.indexOf('pro');
  const mi = out.indexOf('max');
  if (pi >= 0 && mi >= 0) {
    out.splice(Math.max(pi, mi), 1);
    out.splice(Math.min(pi, mi), 1, 'promax');
  }
  return out;
};

// 같은 세대 내 variant 완화 후보 (순서대로 시도)
const variantCandidates = (variants: string[]): string[] => {
  const joined = variants.join('');
  const cands = [joined];
  if (variants.length > 1) for (const v of variants) cands.push(v);
  if (joined === 'promax') cands.push('pro');
  cands.push('');
  return [...new Set(cands)];
};

// ── 공개 API ────────────────────────────────────────────────
/** 모델 썸네일 결정 — 항상 실존 경로 반환 */
export const resolvePhoneImage = (input: PhoneResolveInput): string => {
  const parsed = parsePhoneModelKey(input);
  const sizeOf = (p: ParsedKey) => p.size || '';

  // ① 정규키 완전 일치 (라인+세대+변형+크기)
  if (parsed && parsed.line && (parsed.gen || sizeOf(parsed))) {
    const exact = byKey.get(`${parsed.brand}|${parsed.line}|${parsed.gen}|${parsed.variants.join('')}|${sizeOf(parsed)}`);
    if (exact) return exact.path;
  }

  // ①-b 특수 모델 nameKey 일치 (버디·점프·와이드·퀀텀·폴더·키즈폰 등)
  if (parsed && parsed.brand === 'samsung') {
    for (const nk of parsed.nameKeys || [parsed.nameKey]) {
      if (!nk) continue;
      const special = byNameKey.get(nk);
      if (special) return special.path;
    }
  }

  // ② 카탈로그 image가 실존 파일이면 채택
  const cat = (input.catalogImage || '').trim();
  if (cat) {
    const hit = byDecodedPath.get(cat) || byDecodedPath.get(decodeURIComponent(cat));
    if (hit) return hit.path;
  }

  // ③ 같은 모델(세대) 내 완화 — variant 축소 → 크기 무시 (예: "Z Flip3 5G"→ZFLIP3, "탭 A7 라이트 (8.7)"→A7 LITE)
  if (parsed && parsed.line && parsed.gen) {
    for (const v of variantCandidates(parsed.variants)) {
      for (const sz of sizeOf(parsed) ? [sizeOf(parsed), ''] : ['']) {
        const e = byKey.get(`${parsed.brand}|${parsed.line}|${parsed.gen}|${v}|${sz}`);
        if (e) return e.path;
      }
    }
  }

  // ④ 공용 기본 이미지 (해당 모델 이미지 없음 · 파싱 실패)
  if (parsed && TABLET_LINES.has(parsed.line)) {
    return parsed.brand === 'apple' ? TABLET_DEFAULT_IMAGE_APPLE : TABLET_DEFAULT_IMAGE_SAMSUNG;
  }
  return BRAND_DEFAULT_PHONE_IMAGE;
};

/** 시리즈 카드 대표 이미지 — 해당 라인의 최신 세대 대표 (A/J 등 라인 이미지 없으면 기본) */
export const resolvePhoneSeriesImage = (brand: string, seriesName: string): string => {
  const parsed = parseOneName(seriesName || '', (brand || '').toLowerCase());
  if (!parsed) return BRAND_DEFAULT_PHONE_IMAGE;

  // 아이폰 시리즈명('iPhone 15')은 세대가 특정됨 → 해당 세대 기본형 우선
  if (parsed.brand === 'apple' && parsed.gen) {
    const genEntries = (byLine.get('apple|iphone') || []).filter((e) => e.gen === parsed.gen);
    if (!genEntries.length) return BRAND_DEFAULT_PHONE_IMAGE;
    return (genEntries.find((e) => e.variant === '') || genEntries[0]).path;
  }

  const entries = byLine.get(`${parsed.brand}|${parsed.line}`) || [];
  if (!entries.length) return BRAND_DEFAULT_PHONE_IMAGE;
  const maxGen = entries[entries.length - 1].genOrder;
  const latest = entries.filter((e) => e.genOrder === maxGen);
  // 기존 UI 관례 유지: S 시리즈 카드는 울트라 대표, 그 외 기본형 우선
  return (
    latest.find((e) => e.variant === 'ultra') ||
    latest.find((e) => e.variant === '') ||
    latest[0]
  ).path;
};

/** 경로가 매니페스트에 실존하는 스마트폰 이미지인지 */
export const isKnownPhoneImagePath = (p: string | null | undefined): boolean => {
  if (!p) return false;
  return byDecodedPath.has(p) || byDecodedPath.has(decodeURIComponent(p));
};
